﻿import { FC, ReactElement } from "react";
import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import { NavGroup } from "@am-tax/fe-core";
import { useMatch } from "react-router-dom";
import { IconAffiliate, IconBuildingSkyscraper, IconCoin, IconDashboard, IconLogout, IconSettings, IconUsers, IconUsersGroup } from "@tabler/icons-react";
import { NavItem, NavItemProps } from "./NavItem";
import { CandidatesArea, DashboardArea, EntitiesArea, WageImportsArea } from "../../auth";
import { useAppPermissions } from "../../hooks";
import { useHasAccess } from "../../hooks/useHasAccess";
import { ApplicationRole, useClient } from "../../api";
import { LeftNavCore } from "./LeftNavCore";
import { ClientContextHeader } from "./ClientContextHeader";

export const LeftNav: FC = () => {
    const { hasRole } = useHasAccess();
    const isAdmin = hasRole(ApplicationRole.admin);

    const adminPageMatch = useMatch("/admin/*");
    const selectionPageMatch = useMatch("/clients/*");
    const clientPageMatch = useMatch("/clients/:cId/*");
    const colorScheme = adminPageMatch ? "gray" : "blue";

    const cId = clientPageMatch?.params?.cId;
    const appPermissions = useAppPermissions(cId);
    const { data: clientData } = useClient(cId);

    let items: Array<NavItemProps | NavGroup | ReactElement> = [];
    const itemsFooter: Array<NavItemProps | NavGroup | ReactElement> = [];
    const collapsable = !(selectionPageMatch && !clientPageMatch);

    if (clientPageMatch) {
        items = [
            <ClientContextHeader text="client-context-header" clientName={clientData?.name} colorScheme={colorScheme} />,
            <Box borderBottom="1px solid" borderColor="whiteAlpha.200" mx="0.75rem" mb="0.5rem" />,
            <NavItem
                show={appPermissions.showArea(DashboardArea)}
                {...{ text: "Dashboard", to: `/clients/${cId}`, leftIcon: <IconDashboard size={22} />, end: true, colorScheme }}
            />,
            <NavItem
                show={appPermissions.showArea(CandidatesArea)}
                {...{ text: "Candidates", to: `/clients/${cId}/candidates`, leftIcon: <IconUsersGroup size={22} />, colorScheme }}
            />,
            <NavItem
                show={appPermissions.showArea(EntitiesArea)}
                {...{
                    text: "Entities",
                    to: `/clients/${cId}/entities`,
                    leftIcon: <IconAffiliate size={22} />,
                    colorScheme,
                }}
            />,
            <NavItem
                show={appPermissions.showArea(WageImportsArea)}
                {...{ text: "Payroll", to: `/clients/${cId}/wage-imports`, leftIcon: <IconCoin size={22} />, colorScheme }}
            />,
        ];
    }

    if (isAdmin && adminPageMatch) {
        items = [
            <NavItem
                show
                {...{
                    text: "Dashboard",
                    to: `/admin`,
                    leftIcon: <IconDashboard size={22} />,
                    end: true,
                    colorScheme,
                }}
            />,
            <NavItem
                show
                {...{
                    text: "Clients",
                    to: `/admin/clients`,
                    leftIcon: <IconAffiliate size={22} />,
                    colorScheme,
                }}
            />,
            <NavItem
                show
                {...{
                    text: "Users",
                    to: `/admin/users`,
                    leftIcon: <IconUsers size={22} />,
                    colorScheme,
                }}
            />,
        ];
    }

    if (isAdmin) {
        // Divider line — always visible, no collapsed awareness needed
        itemsFooter.push(<Box borderTop="1px solid" borderColor="whiteAlpha.200" mt="0.25rem" />);

        if (!adminPageMatch) {
            itemsFooter.push(
                <NavItem
                    show
                    {...{
                        text: "System Settings",
                        to: `/admin`,
                        leftIcon: <IconSettings size={22} />,
                        colorScheme,
                    }}
                />
            );
        } else {
            itemsFooter.push(
                <NavItem
                    show
                    {...{
                        text: "Exit System Settings",
                        to: `/clients`,
                        leftIcon: <IconLogout size={22} />,
                        colorScheme,
                    }}
                />
            );
        }
    }

    return (
        <LeftNavCore
            logo={{ type: "global" }}
            appTitle={"Work Opportunity Tax Credit"}
            appTitleProps={{ letterSpacing: 0 }}
            bgGradient={`linear(to-b, ${colorScheme}.900, ${colorScheme}.700)`}
            items={items as any}
            collapsable={collapsable}
            itemsHeader={
                selectionPageMatch &&
                !clientPageMatch && (
                    <Flex height="100%" direction="column" align="center" justify="center" gap={3} px={3} opacity={0.6}>
                        <Icon as={IconBuildingSkyscraper} boxSize={8} color="whiteAlpha.700" />
                        <Text as="em" color="whiteAlpha.800" fontSize="sm" textAlign="center">
                            Select a client to get started
                        </Text>
                    </Flex>
                )
            }
            itemsFooter={itemsFooter as any}
        />
    );
};
